import { useEffect, type LegacyRef, type ReactElement } from 'react';
import assert from 'assert';
import type { StateProps } from './StateComponent';
import StateComponent from './StateComponent';
import type { State } from '../../../store/data/state';
import {
  isDataState,
  isErrorState,
  isIdleState,
  isLoadingState,
} from '../../../store/data/state';
import { requestData, requestDataWithParams } from '../../../store/reducers';
import { useAppDispatch } from '../../../store/hooks';
import {
  ConfigTableByParams,
  isByParams,
  isCachedDataDataState,
  isCachedDataState,
  isCachedErrorState,
  iscachedIdleState,
  isCachedLoadingState,
  isStateOfRecord,
} from '../../../store/data/data';
import { CachedDataState, paramKeyGetter } from '../../../store/data/initials';
import { fetchDataWithParams } from '../../../store/asyncThunks';

export interface PropsByState {
  match: (state: State<unknown, unknown> | CachedDataState<unknown>) => boolean;
  makeProps: (
    state: State<unknown, unknown> | CachedDataState<unknown>
  ) => StateProps;
}

export const matchers: PropsByState[] = [
  {
    match: (s) =>
      (s.type === 'cached' && isCachedErrorState(s)) ||
      (s.type !== 'cached' && isErrorState(s)),
    makeProps: (state) => ({
      name: 'Impossible de charger la ressource externe',
      message: ('message' in state && state.message) || '',
    }),
  },
  {
    match: (s) =>
      (s.type === 'cached' && iscachedIdleState(s)) ||
      (s.type !== 'cached' && isIdleState(s)),
    makeProps: () => ({
      name: 'Veuillez patienter !',
      message: 'Lancement du chargement de la ressource',
      spinner: true,
    }),
  },
  {
    match: (s) =>
      (s.type === 'cached' && isCachedLoadingState(s)) ||
      (s.type !== 'cached' && isLoadingState(s)),
    makeProps: () => ({
      name: 'Veuillez patienter !',
      message: 'Chargement en cours',
      spinner: true,
    }),
  },
];

interface ConfigTableByParamsWithParams<RowType>
  extends ConfigTableByParams<RowType> {
  params: object;
}

interface CachedDataStateWithParams {
  state: CachedDataState<unknown> | undefined;
  url: string;
}

const isState = (
  requirement:
    | State<unknown, unknown>
    | ConfigTableByParamsWithParams<unknown>
    | CachedDataStateWithParams
): requirement is State<unknown, unknown> =>
  Object.keys(requirement).includes('state') &&
  (requirement as CachedDataStateWithParams).state === undefined
    ? false
    : isStateOfRecord(
        Object.keys(requirement).includes('state')
          ? ((requirement as CachedDataStateWithParams)
              .state as CachedDataState<unknown>)
          : (requirement as
              | State<string, Record<string, unknown>>
              | ConfigTableByParamsWithParams<unknown>)
      );
const isConfigTableByParamsWithParams = (
  requirement:
    | State<unknown, unknown>
    | ConfigTableByParamsWithParams<unknown>
    | CachedDataStateWithParams
): requirement is ConfigTableByParamsWithParams<unknown> =>
  Object.keys(requirement).includes('state') &&
  (requirement as CachedDataStateWithParams).state === undefined
    ? false
    : isByParams(
        Object.keys(requirement).includes('state')
          ? ((requirement as CachedDataStateWithParams)
              .state as CachedDataState<unknown>)
          : (requirement as
              | State<string, Record<string, unknown>>
              | ConfigTableByParamsWithParams<unknown>)
      );
const isCached = (
  requirement:
    | State<unknown, unknown>
    | ConfigTableByParamsWithParams<unknown>
    | CachedDataStateWithParams
): requirement is CachedDataStateWithParams =>
  Object.keys(requirement).includes('state') &&
  (requirement as CachedDataStateWithParams).state === undefined
    ? true
    : isCachedDataState(
        Object.keys(requirement).includes('state')
          ? ((requirement as CachedDataStateWithParams)
              .state as CachedDataState<unknown>)
          : (requirement as
              | State<string, Record<string, unknown>>
              | ConfigTableByParamsWithParams<unknown>)
      );

export function StateDecoratorWithoutRef(
  requirements: (
    | State<unknown, unknown>
    | ConfigTableByParamsWithParams<unknown>
    | CachedDataStateWithParams
  )[],
  render: () => ReactElement
): ReactElement {
  const dispatch = useAppDispatch();

  useEffect(() => {
    // requests without params
    requirements
      .filter(isState)
      .filter(isIdleState)
      .forEach((requirement) => dispatch(requestData(requirement)));
    // requests with params
    requirements
      .filter(isConfigTableByParamsWithParams)
      .map(({ params, id, value }) => {
        const key = paramKeyGetter[id].makeParamKey(
          params as Parameters<
            (typeof paramKeyGetter)[typeof id]['makeParamKey']
          >[0]
        );
        const state = value[key];
        return { params, id, state };
      })
      .filter(({ state }) => state === undefined || isIdleState(state))
      .forEach(({ params, id }) =>
        dispatch(requestDataWithParams({ params, id }))
      );
    requirements
      .filter(isCached)
      .filter(({ state }) => state === undefined || iscachedIdleState(state))
      .forEach(({ url }) => dispatch(fetchDataWithParams(url)));
  }, [dispatch, requirements]);

  // Null states
  const nullStates = requirements.filter((value) => !value);

  if (nullStates.length > 0) {
    return <StateComponent name="" message="Chargement impossible !" />;
  }

  // Non-data states
  type StateInfo =
    | {
        _tag: 'NoData';
        currentState: State<unknown, unknown> | CachedDataState<unknown>;
        makePropsForCurrentState: (
          state: State<unknown, unknown> | CachedDataState<unknown>
        ) => StateProps;
      }
    | {
        _tag: 'Data';
      };

  const simpleRequirements = requirements.filter(isState);
  const requirementsWithParams = requirements
    .filter(isConfigTableByParamsWithParams)
    .map(({ params, id, value }) => {
      const key = paramKeyGetter[id].makeParamKey(
        params as Parameters<
          (typeof paramKeyGetter)[typeof id]['makeParamKey']
        >[0]
      );
      const state = value[key];
      return state ?? { type: 'externals', id, _tag: 'idle' };
    });
  const cachedRequirements: CachedDataState<unknown>[] = requirements
    .filter(isCached)
    .map(({ state, url }) => {
      return state !== undefined
        ? state
        : { _tag: 'idle' as const, type: 'cached' as const, id: url };
    });
  const processedRequirements = (
    simpleRequirements as
      | State<unknown, unknown>[]
      | State<string, Record<string, unknown>>[]
      | CachedDataState<unknown>[]
  ).concat(requirementsWithParams, cachedRequirements);

  // The current state is the last state in the list being matched by at least one of the required data
  const stateInfo = matchers.reduce<StateInfo>(
    (acc, matcher) => {
      const matchedRequirement = processedRequirements.reduce<StateInfo>(
        (innerAcc, requirement) => {
          return matcher.match(requirement)
            ? {
                _tag: 'NoData',
                currentState: requirement,
                makePropsForCurrentState: matcher.makeProps,
              }
            : innerAcc;
        },
        acc
      );
      return matchedRequirement;
    },
    { _tag: 'Data' }
  );

  if (stateInfo._tag === 'NoData') {
    const props = stateInfo.makePropsForCurrentState(stateInfo.currentState);
    return <StateComponent {...props} />;
  }

  // Data state
  assert(
    processedRequirements.every(
      (s) =>
        (s.type === 'cached' && isCachedDataDataState(s)) ||
        (s.type !== 'cached' && isDataState(s))
    ),
    'StateDecorator: unloaded data.'
  );

  return render();
}

export default function StateDecorator(
  requirements: (
    | State<unknown, unknown>
    | ConfigTableByParamsWithParams<unknown>
  )[],
  _ref: LegacyRef<HTMLDivElement>,
  render: () => ReactElement
): ReactElement {
  return StateDecoratorWithoutRef(requirements, render);
}
